import { Entypo } from "@expo/vector-icons";
import { DrawerActions, useNavigation } from "@react-navigation/native";
import { Text, TopNavigation, TopNavigationProps } from "@ui-kitten/components";
import { ACCOUNT_STACK_BACKGROUND_COLOR } from "constants/";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import useIsSSO from "hooks/useIsSSO";
import { capitalize } from "lodash";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import {
  Dimensions,
  Platform,
  Pressable,
  RefreshControlProps,
  SafeAreaView,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from "react-native";
import Chevron from "./Chevron";

interface MainContainerProps extends TopNavigationProps {
  style?: ViewStyle | undefined;
  goBackAction?: () => void;
  showLeftAction?: boolean;
  showHeader?: boolean;
  navigationTitle?: React.ReactElement;
  navigationSubtitle?: React.ReactElement;
  goBackArrowDirection?: "right" | "left" | "down";
  refreshControl?:
    | React.ReactElement<
        RefreshControlProps,
        string | React.JSXElementConstructor<any>
      >
    | undefined;
  scrollViewStyle?: ViewStyle;
  grayBackground?: boolean;
  useScrollView?: boolean;
  navigatorClassName?: string;
}

export const getWidth = () => {
  const windowWidth = Dimensions.get("window").width;

  if (windowWidth >= 768) {
    return 600;
  }
  return windowWidth;
};

const MainContainer: React.FC<MainContainerProps> = ({
  children,
  goBackAction,
  showLeftAction = true,
  showHeader = true,
  style,
  goBackArrowDirection,
  accessoryRight,
  refreshControl,
  scrollViewStyle,
  grayBackground = false,
  navigationTitle,
  navigationSubtitle,
  useScrollView = true,
  navigatorClassName,
  ...rest
}) => {
  const { account } = useAccount();
  const navigation = useNavigation();
  const { isSSO } = useIsSSO();

  const state = navigation.getState();

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { tenantThemeColor, tenantSubdomain } = useAppSelector(
    (state) => state.tenant
  );
  const tenantAlias = account?.members[0].tenant.alias;

  const BackChevron = ({
    direction = "left",
  }: {
    direction?: "right" | "left" | "down";
  }) => (
    <Pressable
      onPress={() => {
        if (Platform.OS !== "web" || !goBackAction) {
          navigation.goBack();
        } else {
          goBackAction();
        }
      }}
    >
      <Chevron direction={direction} color="white"></Chevron>
    </Pressable>
  );

  const renderTitle = () => {
    if (navigationTitle) return navigationTitle;
    return (
      <Text style={{ color: "white", fontSize: 20 }}>
        {tenantConfig?.programName ||
          capitalize(tenantAlias || tenantSubdomain || "") + " Member"}
      </Text>
    );
  };

  const renderSubtitle = () => {
    if (navigationSubtitle) return navigationSubtitle;
    return <></>;
  };

  const contentContainer = () => {
    const containerStyle: StyleProp<ViewStyle> = {
      alignSelf: "center",
      width: "100%",
      maxWidth: 768,
      flexGrow: 1,
    };

    if (useScrollView) {
      return (
        <ScrollView
          testID="main-container-scrollview"
          refreshControl={refreshControl}
          contentContainerStyle={[scrollViewStyle, containerStyle]}
        >
          {children}
        </ScrollView>
      );
    }
    return <View style={containerStyle}>{children}</View>;
  };

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <View
        style={{
          flex: 1,
          overflow: "hidden",
          backgroundColor: grayBackground
            ? ACCOUNT_STACK_BACKGROUND_COLOR
            : "white",
          ...style,
        }}
      >
        {showHeader && (
          <View className={navigatorClassName}>
            <TopNavigation
              {...rest}
              style={{
                backgroundColor: tenantThemeColor,
                shadowColor: "#000",
                shadowOffset: {
                  width: 0,
                  height: 3,
                },
                shadowOpacity: 0.29,
                shadowRadius: 4.65,
                elevation: 7,
              }}
              title={renderTitle}
              subtitle={renderSubtitle}
              //@ts-ignore
              accessoryLeft={
                showLeftAction && (state.index > 0 || goBackAction) ? (
                  <BackChevron direction={goBackArrowDirection} />
                ) : (
                  <></>
                )
              }
              accessoryRight={
                accessoryRight /**|| isSmallScreen */
                  ? accessoryRight
                  : () => (
                      <Pressable
                        onPress={() =>
                          navigation.dispatch(DrawerActions.toggleDrawer())
                        }
                      >
                        {isSSO && (
                          <Entypo name="menu" size={24} color="white"></Entypo>
                        )}
                      </Pressable>
                    )
              }
            ></TopNavigation>
          </View>
        )}
        <ScrollView
          testID="main-container-scrollview"
          refreshControl={refreshControl}
          contentContainerStyle={[
            scrollViewStyle,
            {
              alignSelf: "center",
              width: "100%",
              maxWidth: 768,
              flexGrow: 1,
            },
          ]}
        >
          {children}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default MainContainer;
